'use client';

import { useId, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { ArrowUpRight } from '@phosphor-icons/react';
import './about-food-story.css';

const ingredients = [
  {
    name: 'Лосось',
    text: 'Нежный слой лосося снаружи ролла.',
    x: 54,
    y: 22,
  },
  {
    name: 'Творожный сыр',
    text: 'Мягкая сливочная начинка в центре ролла.',
    x: 49,
    y: 53,
  },
  {
    name: 'Огурец',
    text: 'Свежий огурец добавляет хруст и дополняет творожный сыр.',
    x: 34,
    y: 42,
  },
  {
    name: 'Рис и нори',
    text: 'Слой риса и тонкий лист нори вокруг начинки.',
    x: 19,
    y: 51,
  },
];

export function AboutFoodStory() {
  const [active, setActive] = useState(0);
  const id = useId();
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const selected = ingredients[active];
  const handleKey = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    let next = index;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown')
      next = (index + 1) % ingredients.length;
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp')
      next = (index - 1 + ingredients.length) % ingredients.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = ingredients.length - 1;
    else return;
    event.preventDefault();
    setActive(next);
    refs.current[next]?.focus();
  };

  return (
    <section className="ab-food" id="ab-story" aria-labelledby={id + '-title'}>
      <div className="ab-food-copy">
        <span className="ab-food-eyebrow">Начнём с главного</span>
        <h2 id={id + '-title'}>
          Посмотрите,
          <br />
          что внутри.
        </h2>
        <p>
          Мы специализируемся только на роллах. Выбираем проверенные ингредиенты — и делаем продукт,
          за которым возвращаются.
        </p>
      </div>
      <div className="ab-food-art">
        <span className="ab-food-product-name">Филадельфия</span>
        <Image
          src="/storefront/about-philadelphia-macro-v1.webp"
          alt="Филадельфия в разрезе: лосось, творожный сыр, огурец, рис и нори"
          width={1100}
          height={1100}
          sizes="(max-width:760px) 112vw, 760px"
        />
        <div
          className="ab-food-pin"
          style={{ '--pin-x': selected.x + '%', '--pin-y': selected.y + '%' } as CSSProperties}
          aria-hidden="true"
        >
          <span className="ab-food-pin-dot" />
          <span className="ab-food-pin-label">{selected.name}</span>
        </div>
        <span className="ab-food-art-caption">Любимый ролл. Крупным планом.</span>
      </div>
      <div className="ab-food-picker" role="tablist" aria-label="Ингредиенты Филадельфии">
        {ingredients.map((ingredient, index) => (
          <button
            key={ingredient.name}
            ref={(element) => {
              refs.current[index] = element;
            }}
            type="button"
            role="tab"
            id={id + '-tab-' + index}
            aria-controls={id + '-panel-' + index}
            aria-selected={active === index}
            tabIndex={active === index ? 0 : -1}
            onClick={() => setActive(index)}
            onKeyDown={(event) => handleKey(event, index)}
          >
            <span className="ab-food-picker-dot" />
            {ingredient.name}
          </button>
        ))}
      </div>
      <div className="ab-food-details">
        {ingredients.map((ingredient, index) => (
          <p
            key={ingredient.name}
            role="tabpanel"
            id={id + '-panel-' + index}
            aria-labelledby={id + '-tab-' + index}
            hidden={active !== index}
          >
            {ingredient.text}
          </p>
        ))}
        <Link href="/#menu">
          Найти свой ролл <ArrowUpRight size={20} />
        </Link>
      </div>
    </section>
  );
}
